import { Box, useToast, VStack } from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { mealPlanMolecule } from '@web-app/atoms/charting/mealPlanMolecule';
import { sidePaneMolecule } from '@web-app/atoms/in-session/sidePaneMolecule';
import { useDailyRoom } from '@web-app/components/CallV2/hooks/useDailyRoom';
import { MealPlanInSessionDrawer } from '@web-app/components/CallV2/MealPlanInSessionDrawer/MealPlanInSessionDrawer';
import { AppointmentStatusCard } from '@web-app/components/CallV2/SessionPrepTab/AppointmentStatusCard';
import { CrossSellBookingLinkCard } from '@web-app/components/CallV2/SessionPrepTab/CrossSellBookingLinkCard';
import { FactorMealSummaryCard } from '@web-app/components/CallV2/SessionPrepTab/FactorMealSummaryCard';
import { MealPlanCard } from '@web-app/components/CallV2/SessionPrepTab/MealPlanCard';
import { SessionFocusCard } from '@web-app/components/CallV2/SessionPrepTab/SessionFocusCard';
import { SessionFocusWithJournalActivity } from '@web-app/components/CallV2/SessionPrepTab/SessionFocusWithJournalActivity';
import { SessionPrepCardSkeleton } from '@web-app/components/CallV2/SessionPrepTab/SessionPrepCardSkeleton';
import { useSessionPrepStepConfig } from '@web-app/components/CallV2/SessionPrepTab/useSessionPrepStepConfig';
import { SidePaneTabId } from '@web-app/components/CallV2/sidePaneTabIds';
import { isBridgeCaseOpen } from '@web-app/components/MedicareBridge/case-visibility';
import { MedicareBridgePrepStep } from '@web-app/components/MedicareBridge/MedicareBridgePrepStep';
import { TAGS } from '@web-app/components/PatientTags/constants';
import { SessionGoals } from '@web-app/components/SessionGoals/SessionGoals';
import { SessionRecapContent } from '@web-app/components/SessionRecap/SessionRecapContent';
import { useMedicareBridgeCase } from '@web-app/hooks/medicare-bridge/useMedicareBridgeCase';
import { AppointmentResponse } from '@web-app/hooks/queries/appointment-by-room-name/types';
import { useWalmartDocuments } from '@web-app/hooks/session-prep/useWalmartDocuments';
import { useFactorMealSummary } from '@web-app/hooks/useFactorMealSummary';
import { useSessionGuidance } from '@web-app/hooks/useSessionGuidance';
import { useSessionRecap } from '@web-app/hooks/useSessionRecap';
import { SESSION_PREP_LOG_PREFIX } from '@web-app/utils/api/logger-prefixes';
import { useShowMealSummaryOnPrep } from '@web-app/utils/feature-flags/meal-summary-on-prep';
import { useWalmartShoppingInsights } from '@web-app/utils/feature-flags/walmart-shopping-insights';
import {
  getAppointmentTags,
  hasExactPatientTag,
  hasPatientTag,
} from '@web-app/utils/patient-tags.utils';
import { useMolecule } from 'bunshi/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { FC, useCallback, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

export interface SessionPrepTabProps {
  /**
   * Appointment data for the current session.
   * Used to extract patient ID and appointment ID for goal widget.
   */
  appointment?: AppointmentResponse;
}

/**
 * Session Prep tab content for the CallV2 SidePane.
 * Displays session preparation context including:
 * - Session focus and tasks (based on session number)
 * - Last session recap (AI-generated highlights)
 * - Current goals status
 *
 * When in-session scheduling flag is enabled, content is organized into steps.
 * In prescriber flow the SMART goals step is hidden and the remaining steps
 * shift up: Schedule next session becomes step 1, Meal plan becomes step 2.
 *
 * Each section loads independently with its own skeleton loader.
 */
export const SessionPrepTab: FC<SessionPrepTabProps> = ({ appointment }) => {
  const patientId = appointment?.client_id?.toString() || null;

  useEffect(
    function logPatientIdOnLoad() {
      if (!patientId) return;
      datadogLogs.logger.info(
        `${SESSION_PREP_LOG_PREFIX} Session prep tab loaded`,
        {
          patientId,
          healthieAppointmentId: appointment?.healthie_id,
        },
      );
    },
    [patientId, appointment?.healthie_id],
  );
  const [isMealPlanDrawerOpen, setIsMealPlanDrawerOpen] = useState(false);
  const { roomId } = useDailyRoom();

  // ---------------------------------------------------------------------------
  // Data hooks — each fetches independently, React Query parallelizes
  // ---------------------------------------------------------------------------

  const {
    sessionInfo,
    sessionGuidance,
    isLoading: isInfoGuidanceLoading,
    error: sessionInfoGuidanceError,
  } = useSessionGuidance(patientId);

  const {
    sessionRecap,
    isLoading: isRecapLoading,
    error: sessionRecapError,
  } = useSessionRecap(patientId);

  const showFactorMealSummary = useShowMealSummaryOnPrep();
  const {
    mealSummary: factorMealSummary,
    isLoading: isFactorMealSummaryLoading,
    error: factorMealSummaryError,
  } = useFactorMealSummary(patientId, showFactorMealSummary);

  // Prefetch only — warms the React Query cache (and the presigned download
  // links) so the Patient tab's insights banner renders without a fetch wait.
  // Result is intentionally unused until the Walmart documents card lands.
  const showWalmartInsights = useWalmartShoppingInsights();
  const isWalmartPatient = hasPatientTag(
    getAppointmentTags(appointment),
    TAGS.WALMART,
  );
  // PRE-GLP-1 means the patient asked about GLP-1 when booking (variant 1a).
  const glp1Variant = hasExactPatientTag(
    getAppointmentTags(appointment),
    TAGS.PRE_GLP_1,
  )
    ? 'interested'
    : 'eligible';
  useWalmartDocuments({
    patientId,
    appointmentId: appointment?.healthie_id?.toString(),
    enabled: showWalmartInsights && isWalmartPatient,
  });

  // ---------------------------------------------------------------------------
  // Step config + molecules for side pane / meal plan
  // ---------------------------------------------------------------------------

  const bridgeQuery = useMedicareBridgeCase(patientId);
  const hasOpenBridgeCase = isBridgeCaseOpen(bridgeQuery.data);

  const stepConfig = useSessionPrepStepConfig({
    hasRoomId: Boolean(roomId),
    isRecapLoading,
    hasSessionRecap: Boolean(sessionRecap),
    hasOpenBridgeCase,
    // isPending stays true while the query is disabled (Clerk not loaded yet)
    // and while the first fetch is in flight. isLoading is false in the
    // disabled wait, which would still flash Goals as step 1.
    isBridgeCaseLoading: Boolean(patientId) && bridgeQuery.isPending,
  });

  const { activeSidePaneTabAtom } = useMolecule(sidePaneMolecule);
  const setActiveTab = useSetAtom(activeSidePaneTabAtom);

  const { isStandalonePageOpenAtom } = useMolecule(mealPlanMolecule);
  const isStandalonePageOpen = useAtomValue(isStandalonePageOpenAtom);
  const toast = useToast();

  const handleMealPlanOpen = useCallback(() => {
    if (isStandalonePageOpen) {
      // Try to focus the existing standalone tab via its named target
      window.open(`/call/meal-plan/${roomId}`, `meal-plan-${roomId}`);
      toast({
        title: 'Meal plan is open in another tab',
        status: 'info',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } else {
      setIsMealPlanDrawerOpen(true);
    }
  }, [isStandalonePageOpen, roomId, toast]);

  return (
    <Box
      p={4}
      pb={10}
      height="100%"
      overflowY="auto"
      data-testid="session-prep-tab-content"
    >
      <VStack spacing={5} align="stretch">
        <ErrorBoundary
          fallback={
            <SessionFocusCard
              error={sessionInfoGuidanceError}
              sessionInfo={sessionInfo}
              sessionGuidance={sessionGuidance}
              isLoading={isInfoGuidanceLoading}
              journalActivity={null}
              onJournalClick={() => setActiveTab(SidePaneTabId.JOURNAL)}
            />
          }
        >
          <SessionFocusWithJournalActivity
            error={sessionInfoGuidanceError}
            sessionInfo={sessionInfo}
            sessionGuidance={sessionGuidance}
            isLoading={isInfoGuidanceLoading}
            onJournalClick={() => setActiveTab(SidePaneTabId.JOURNAL)}
          />
        </ErrorBoundary>

        <SessionRecapContent
          error={sessionRecapError}
          sessionRecap={sessionRecap}
          isLoading={isRecapLoading}
          patientId={patientId}
        />

        {showFactorMealSummary && (
          <ErrorBoundary fallback={null}>
            <FactorMealSummaryCard
              error={factorMealSummaryError}
              factorMealSummary={factorMealSummary}
              isLoading={isFactorMealSummaryLoading}
              patientId={patientId}
            />
          </ErrorBoundary>
        )}

        {stepConfig.medicareBridge.isVisible && bridgeQuery.data && (
          <Box
            bg="surface.card"
            border="1px solid"
            borderColor="border.default"
            borderRadius="2xl"
            p={6}
            data-testid="session-prep-medicare-bridge"
          >
            <MedicareBridgePrepStep
              stepNumber={stepConfig.medicareBridge.stepNumber}
              bridgeCase={bridgeQuery.data}
            />
          </Box>
        )}

        {stepConfig.goals.isVisible && (
          <Box
            bg="surface.card"
            border="1px solid"
            borderColor="border.default"
            borderRadius="2xl"
            p={6}
          >
            {patientId ? (
              <SessionGoals
                patientId={patientId}
                appointmentId={appointment?.healthie_id?.toString()}
                showArchived={false}
                variant="card"
                stepNumber={stepConfig.goals.stepNumber}
              />
            ) : (
              <SessionPrepCardSkeleton rows={3} />
            )}
          </Box>
        )}

        {stepConfig.schedule.isVisible && (
          <Box
            bg="surface.card"
            border="1px solid"
            borderColor="border.default"
            borderRadius="2xl"
            p={6}
            data-testid="session-prep-scheduling-card"
          >
            <AppointmentStatusCard
              stepNumber={stepConfig.schedule.stepNumber}
              patientId={patientId}
              patientName={appointment?.healthie?.user?.name ?? ''}
              excludeAppointmentId={appointment?.healthie_id?.toString()}
            />

            <ErrorBoundary fallback={null}>
              <CrossSellBookingLinkCard
                sourceAppointmentHealthieId={appointment?.healthie_id?.toString()}
                patientHealthieId={patientId}
                patientFirstName={appointment?.healthie?.user?.first_name}
                glp1Variant={glp1Variant}
              />
            </ErrorBoundary>
          </Box>
        )}

        {stepConfig.mealPlan.isVisible && (
          <Box
            bg="surface.card"
            border="1px solid"
            borderColor="border.default"
            borderRadius="2xl"
            p={6}
          >
            {appointment?.client_id && sessionRecap && roomId ? (
              <>
                <MealPlanCard
                  stepNumber={stepConfig.mealPlan.stepNumber}
                  onOpen={handleMealPlanOpen}
                />
                <MealPlanInSessionDrawer
                  isOpen={isMealPlanDrawerOpen}
                  onClose={() => setIsMealPlanDrawerOpen(false)}
                  dailyRoomName={roomId}
                  patientName={appointment?.healthie?.user?.name}
                  placement="left"
                />
              </>
            ) : (
              <SessionPrepCardSkeleton />
            )}
          </Box>
        )}
      </VStack>
    </Box>
  );
};

SessionPrepTab.displayName = 'SessionPrepTab';
